import { useCallback, useEffect, useState } from "react";
import { unwrapResult } from "@reduxjs/toolkit";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import "./jobs.scss";

import { getEmploymentTypes, suggestJob } from "../../redux/state/jobSlice";
import CustomPhoneInput from "../../uiKit/Input/CustomPhoneInput";
import { onlyNumbers, validateEmail, validateName } from "../../helpers/validation";
import { isPossiblePhoneNumber } from "react-phone-number-input";
import CustomInput from "../../uiKit/Input/CustomInput";
import CustomSelect from "../../uiKit/CustomSelect/CustomSelect";
import Title from "../../uiKit/Title/Title";
import CustomButton from "../../uiKit/Button/CustomButton";
import { gray650, gray900 } from "../../uiKit/assets/colors/colors";
import SuggestJobWarning from "./suggestJobWarning";

const SuggestJob = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  const isLoading = useSelector((state) => state.job.isLoading, shallowEqual);
  const employmentTypeList = useSelector((state) => state.job.employmentTypes, shallowEqual);

  const [job, setJob] = useState({
    title: "",
    contactPerson: "",
    location: "",
    employmentType: 1,
    compensation: 0,
    description: "",
    email: "",
    phoneNumber: "",
  });

  const [error, setError] = useState({
    title: "",
    contactPerson: "",
    compensation: "",
    description: "",
    email: "",
    phoneNumber: "",
    location: "",
  });

  useEffect(() => {
    dispatch(getEmploymentTypes());
  }, [dispatch]);

  const suggestHandler = () => {
    dispatch(suggestJob({ ...job }))
      .then(unwrapResult)
      .then(() => {
        toast.info("Success");
        history.goBack();
      })
      .catch(({ title, errors }) => {
        toast.info(title);
        if (!errors) return;
        toast.info(Object.values(errors).join(" "));
      });
  };

  const onTitleChange = useCallback((e) => {
    const title = e.target.value;
    setJob((prev) => ({ ...prev, title }));
    if (!title) {
      setError((prev) => ({ ...prev, title: "Required" }));
      return;
    }

    if (!validateName(title)) {
      setError((prev) => ({ ...prev, title: "Invalid title" }));
    } else {
      setError((prev) => ({ ...prev, title: "" }));
    }
  }, []);

  const onLocationChange = useCallback((e) => {
    const location = e.target.value;
    setJob((prev) => ({ ...prev, location }));
    if (!location) {
      setError((prev) => ({ ...prev, location: "Required" }));
      return;
    }

    if (!validateName(location)) {
      setError((prev) => ({ ...prev, location: "Invalid location" }));
    } else {
      setError((prev) => ({ ...prev, location: "" }));
    }
  }, []);

  const onContactPersonChange = useCallback((e) => {
    const contactPerson = e.target.value;
    setJob((prev) => ({ ...prev, contactPerson }));
    if (!contactPerson) {
      setError((prev) => ({ ...prev, contactPerson: "Required" }));
      return;
    }

    if (!validateName(contactPerson)) {
      setError((prev) => ({ ...prev, contactPerson: "Invalid name" }));
    } else {
      setError((prev) => ({ ...prev, contactPerson: "" }));
    }
  }, []);

  const amountChangeHandler = (e) => {
    const value = e.target.value;
    if (onlyNumbers(value)) {
      setJob((prev) => ({ ...prev, compensation: !!parseInt(value) ? parseInt(value) : 0 }));
      setError((prev) => ({ ...prev, compensation: "" }));
    } else {
      if (!value) {
        setError((prev) => ({ ...prev, compensation: "Required" }));
      } else {
        setError((prev) => ({ ...prev, compensation: "Invalid amount" }));
      }
    }
  };

  const validateAmount = () => {
    if (!job.compensation) {
      setError((prev) => ({ ...prev, compensation: "Required" }));
    }
  };

  const onEmailChange = useCallback(
    (e) => {
      const email = e.target.value;
      setJob((prev) => ({ ...prev, email }));

      if (!email) {
        if (!error.phoneNumber) {
          setError((prev) => ({ ...prev, email: "" }));
        }

        if (!job.phoneNumber) {
          setError((prev) => ({ ...prev, email: "Email or Phone required" }));
        }
        return;
      }

      if (!validateEmail(email)) {
        setError((prev) => ({ ...prev, email: "Invalid email address" }));
      } else {
        setError((prev) => ({ ...prev, email: "", phoneNumber: "" }));
      }
    },
    [job, error]
  );

  const onPhoneNumberChange = useCallback(
    (value) => {
      setJob((prev) => ({ ...prev, phoneNumber: value }));

      if (!value) {
        if (!job.email) {
          setError((prev) => ({ ...prev, email: "Email or Phone required" }));
        } else {
          setError((prev) => ({ ...prev, email: "" }));
        }

        return;
      }

      if (!isPossiblePhoneNumber(value)) {
        setError((prev) => ({ ...prev, phoneNumber: "Invalid phone number" }));
      } else {
        setError((prev) => ({ ...prev, email: "", phoneNumber: "" }));
      }
    },
    [job]
  );

  return (
    <>
      <div className="breadcrumbWrapper">
        <Title text="Jobs" fontWeight={500} color={gray650} onClick={() => history.push("/jobs")} />
        <Title text=">" fontWeight={500} color={gray650} />
        <Title text={"Suggest a Job"} color={gray900} fontWeight={500} />
      </div>
      <div className="suggestJobWrapper">
        <Title
          text="Suggest a job"
          fontSize={32}
          lineHeight={40}
          style={{ marginBottom: "32px" }}
        />
        <div className="jobFormWrapper">
          <div className="inputs">
            <CustomInput
              label="Job title"
              onChange={onTitleChange}
              error={!!error.title}
              errorMessage={error.title}
              onFocus={() => setError((prev) => ({ ...prev, title: "" }))}
              value={job.title}
              disabled={isLoading}
              loading={isLoading}
            />
            <div className="inputGroup">
              <CustomInput
                label="Contact person"
                value={job.contactPerson}
                onChange={onContactPersonChange}
                errorMessage={error.contactPerson}
                onFocus={() => setError((prev) => ({ ...prev, contactPerson: "" }))}
                disabled={isLoading}
                loading={isLoading}
              />
              <CustomInput
                label="Location"
                onChange={onLocationChange}
                value={job.location}
                error={!!error.location}
                errorMessage={error.location}
                disabled={isLoading}
                loading={isLoading}
              />
            </div>
            <div className="inputGroup">
              <CustomSelect
                label="Type of employment"
                data={employmentTypeList}
                onChange={(e) => setJob((prev) => ({ ...prev, employmentType: e.target.value }))}
                value={job.employmentType}
                disabled={isLoading}
                loading={isLoading}
              />
              <CustomInput
                label="Compensation (monthly)"
                onChange={amountChangeHandler}
                value={job.compensation}
                error={!!error.compensation}
                errorMessage={error.compensation}
                outFocus={validateAmount}
                onFocus={() => setError((prev) => ({ ...prev, compensation: "" }))}
                maxLength={12}
                disabled={isLoading}
                loading={isLoading}
                inputMode={"numeric"}
              />
            </div>
            <CustomInput
              multiline
              label="Description"
              onChange={(e) => setJob((prev) => ({ ...prev, description: e.target.value }))}
              maxLength={2000}
              value={job.description}
              disabled={isLoading}
              loading={isLoading}
            />
            <div className="inputGroup">
              <CustomInput
                label="Email"
                type="email"
                onChange={onEmailChange}
                value={job.email}
                error={!!error.email}
                errorMessage={error.email}
                disabled={isLoading}
                loading={isLoading}
              />
              <CustomPhoneInput
                label="Phone"
                onChange={onPhoneNumberChange}
                value={job.phoneNumber}
                error={!!error.phoneNumber}
                errorMessage={error.phoneNumber}
                disabled={isLoading}
                loading={isLoading}
              />
            </div>
            <SuggestJobWarning />
          </div>
          <div className="actions">
            <CustomButton
              title="Send suggestion"
              onClick={suggestHandler}
              disabled={isLoading || Object.values(error).some((x) => !!x)}
              loading={isLoading}
            />
            <CustomButton
              isWhite
              title="Cancel"
              onClick={() => history.goBack()}
              loading={isLoading}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default SuggestJob;
